import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function JoinPage() {
  const { inviteCode } = useParams();
  const { user, guestLogin, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [guestName, setGuestName] = useState("");
  const [loading, setLoading] = useState(false);
  const isRaidInvite = inviteCode?.toUpperCase().startsWith("RAID-");

  const doJoin = async () => {
    setLoading(true);
    try {
      if (isRaidInvite) {
        const { data } = await api.post(`/raids/join/${inviteCode}`);
        navigate(`/raid/${data.raid.id}`);
      } else {
        const { data } = await api.post(`/games/join/${inviteCode}`, { display_name: guestName || undefined });
        navigate(`/game/${data.game.id}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestJoin = async () => {
    if (!guestName.trim()) {
      toast.error("Enter a display name");
      return;
    }
    setLoading(true);
    try {
      await guestLogin(guestName);
      await doJoin();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5] flex items-center justify-center px-6">
      <div className="w-full max-w-md panel-wood rounded-lg p-8">
        <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Invite Code {inviteCode?.toUpperCase()}</p>
        <h1 className="font-display text-3xl mb-6">{isRaidInvite ? "You've been invited to a Raid" : "You've been invited to a game"}</h1>

        {user ? (
          <Button data-testid="join-account-button" onClick={doJoin} disabled={loading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
            Join as {user.name}
          </Button>
        ) : isRaidInvite ? (
          <div className="space-y-4">
            <p className="text-sm text-[#f1e0c5]/60">Joining a Raid as a General requires a full account (you'll be running your own Branch as a GM).</p>
            <Link to="/login" data-testid="join-login-link">
              <Button className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] uppercase tracking-widest text-sm">Log In or Create an Account</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              data-testid="join-guest-name-input"
              placeholder="Your display name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="bg-black/30 border-white/10"
            />
            <Button data-testid="join-guest-button" onClick={handleGuestJoin} disabled={loading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
              Join as Guest
            </Button>
            <p className="text-sm text-center text-[#f1e0c5]/60">
              Have an account?{" "}
              <Link to="/login" data-testid="join-login-link" className="text-[#D4AF37] hover:underline">
                Log in first
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
